// Das Menü, das erscheint, sobald die Maus frei ist (Escape oder Fokus weg): Weiterspielen,
// Spiegel, wer gerade da ist (task-016), Steuerung und Abmelden. Es tritt an die Stelle des früheren
// Hinweises „Klicken zum Spielen“ (ADR 0012, Punkt 6; docs/architektur-paket-2.md, Abschnitt „Client“,
// `hud/`).
//
// Der dunkle Grund lässt Klicks durch (`pointer-events: none`), nur die Karte fängt sie: Ein Klick
// neben die Karte fängt die Maus wieder – wie vorher –, ein Klick auf einen Knopf tut, was
// draufsteht.

import { KEYS, keyLabel } from '../input/keys.js';
import { listenFueller, type AnwesenheitsZeile } from './anwesenheitsliste.js';

export interface MenuOptionen {
  /** „Weiter spielen“: die Maus wieder fangen. */
  beiWeiterSpielen(): void;
  /** „Spiegel“: den Avatar-Editor öffnen (hud/spiegel.ts). */
  beiSpiegel(): void;
  /** „Abmelden“: Sitzung beenden und zurück zur Anmeldeseite. */
  beiAbmelden(): void;
}

export interface Menu {
  setVisible(visible: boolean): void;
  /** Kurzer Hinweis unter den Knöpfen, etwa wenn das Abmelden am Server scheitert; `null` blendet ihn aus. */
  setHinweis(text: string | null): void;
  /** Wer gerade da ist (task-016); darf je Bild aufgerufen werden. */
  setAnwesende(zeilen: readonly AnwesenheitsZeile[]): void;
}

/** Zeilen der Tastenbelegung: Taste(n) und Wirkung. */
const BELEGUNG: readonly [string, string][] = [
  [[KEYS.forward, KEYS.left, KEYS.back, KEYS.right].map(keyLabel).join(' '), 'gehen'],
  ['Maus', 'umsehen'],
  [keyLabel(KEYS.jump), 'springen, beim Fliegen aufwärts'],
  [keyLabel(KEYS.down), 'beim Fliegen abwärts'],
  [keyLabel(KEYS.fly), 'Fliegen an und aus'],
  [keyLabel(KEYS.sicht), 'Ego-Sicht und Schulterkamera wechseln'],
  [`${keyLabel(KEYS.liste)} halten`, 'zeigen, wer da ist'],
  [keyLabel(KEYS.saetze), 'einen Satz sagen'],
  [keyLabel('Escape'), 'Menü zeigen, Maus freigeben'],
];

export function createMenu(parent: HTMLElement, optionen: MenuOptionen): Menu {
  const wurzel = document.createElement('div');
  wurzel.className = 'hud-menu';

  const karte = document.createElement('div');
  karte.className = 'hud-menu-karte';

  const titel = document.createElement('h1');
  titel.textContent = 'Pause';

  const anleitung = document.createElement('p');
  anleitung.className = 'hud-menu-anleitung';
  anleitung.textContent = 'Klicke ins Bild oder auf „Weiter spielen“.';

  const weiter = document.createElement('button');
  weiter.type = 'button';
  weiter.className = 'hud-menu-knopf';
  weiter.textContent = 'Weiter spielen';
  weiter.addEventListener('click', () => optionen.beiWeiterSpielen());

  const spiegel = document.createElement('button');
  spiegel.type = 'button';
  spiegel.className = 'hud-menu-knopf hud-menu-knopf-zweit';
  spiegel.textContent = 'Spiegel';
  spiegel.addEventListener('click', () => optionen.beiSpiegel());

  // Wer gerade da ist (task-016): dieselbe Liste wie mit Tab, hier ohne Taste lesbar.
  const anwesendeTitel = document.createElement('h2');
  anwesendeTitel.textContent = 'Wer ist da';
  const anwesende = document.createElement('ul');
  anwesende.className = 'hud-menu-liste';
  const fuelleAnwesende = listenFueller(anwesende);

  const steuerung = document.createElement('h2');
  steuerung.textContent = 'Steuerung';

  const liste = document.createElement('dl');
  for (const [taste, wirkung] of BELEGUNG) {
    const dt = document.createElement('dt');
    dt.textContent = taste;
    const dd = document.createElement('dd');
    dd.textContent = wirkung;
    liste.append(dt, dd);
  }

  const abmelden = document.createElement('button');
  abmelden.type = 'button';
  abmelden.className = 'hud-menu-knopf hud-menu-knopf-still';
  abmelden.textContent = 'Abmelden';
  abmelden.addEventListener('click', () => optionen.beiAbmelden());

  const hinweis = document.createElement('p');
  hinweis.className = 'hud-menu-hinweis';
  hinweis.hidden = true;

  karte.append(titel, anleitung, weiter, spiegel, anwesendeTitel, anwesende, steuerung, liste, abmelden, hinweis);
  wurzel.append(karte);
  parent.append(wurzel);

  return {
    setVisible(visible) {
      wurzel.hidden = !visible;
    },
    setHinweis(text) {
      hinweis.textContent = text ?? '';
      hinweis.hidden = text === null;
    },
    setAnwesende: fuelleAnwesende,
  };
}
